import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, Tooltip as CTooltip, Filler
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, CTooltip, Filler);

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const hoyAnio = () => new Date().getFullYear();
const hoyMes  = () => new Date().getMonth() + 1;

// ── Sel custom con portal + fix mousedown ─────────────────────────────────────
const Sel = ({ value, onChange, children, w = 120 }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const opciones = React.Children.map(children, ch => ({
    value: String(ch.props.value ?? ''), label: ch.props.children,
  })) || [];
  const selLabel = opciones.find(o => o.value === String(value || ''))?.label || '';
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const handleOpen = () => {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    setOpen(v => !v);
  };
  return (
    <Box ref={ref} sx={{ position: 'relative', width: w }}>
      <Box onClick={handleOpen}
        sx={{ height: 34, px: 1.2, pr: 3.5, display: 'flex', alignItems: 'center',
          border: `1px solid ${open ? IOS.blue : IOS.separator}`, borderRadius: '8px',
          bgcolor: IOS.surface, cursor: 'pointer', userSelect: 'none', position: 'relative',
          boxShadow: open ? `0 0 0 3px ${IOS.blue}18` : 'none', transition: 'all 0.15s' }}>
        <Typography sx={{ fontSize: '0.8rem', color: IOS.label, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis' }}>{selLabel}</Typography>
        <Box sx={{ position: 'absolute', right: 9, top: '50%',
          transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, transition: 'transform 0.2s' }}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1l4 4 4-4" stroke={IOS.label2} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </Box>
      </Box>
      {open && rect && ReactDOM.createPortal(
        <Box sx={{ position: 'fixed', top: rect.bottom + 4, left: rect.left, zIndex: 99999,
          bgcolor: IOS.surface, border: `1px solid ${IOS.separator}`, borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.13)', minWidth: rect.width, maxHeight: 240,
          overflowY: 'auto', '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { bgcolor: IOS.separator, borderRadius: 4 } }}>
          {opciones.map(op => (
            <Box key={op.value}
              onMouseDown={e => {
                e.preventDefault();
                onChange({ target: { value: op.value } });
                setOpen(false);
              }}
              sx={{ px: 1.5, py: 0.9, cursor: 'pointer', whiteSpace: 'nowrap',
                bgcolor: String(value || '') === op.value ? `${IOS.blue}08` : 'transparent',
                '&:hover': { bgcolor: IOS.bg } }}>
              <Typography sx={{ fontSize: '0.8rem',
                color: String(value || '') === op.value ? IOS.blue : IOS.label,
                fontWeight: String(value || '') === op.value ? 700 : 400 }}>{op.label}</Typography>
            </Box>
          ))}
        </Box>,
        document.body
      )}
    </Box>
  );
};

// ── useCountUp ────────────────────────────────────────────────────────────────
const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const diff  = target - start;
    if (!diff) return;
    const t0 = performance.now();
    const tick = now => {
      const p    = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(start + diff * ease);
      if (p < 1) requestAnimationFrame(tick);
      else { prev.current = target; setVal(target); }
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return val;
};

// ── KpiCard ───────────────────────────────────────────────────────────────────
const KpiCard = ({ label, valor, color, icon, esMonto = true, sub }) => {
  const anim = useCountUp(Number(valor) || 0, 800);
  return (
    <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2,
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2,
            letterSpacing: '0.07em', textTransform: 'uppercase', mb: 0.6 }}>{label}</Typography>
          {esMonto
            ? <Typography sx={{ fontWeight: 800, color, fontSize: '1.1rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                S/ {anim.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            : <Typography sx={{ fontWeight: 800, color, fontSize: '1.4rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(anim)}
              </Typography>
          }
          {sub && <Typography sx={{ fontSize: '0.65rem', color: IOS.label2, mt: 0.4 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 38, height: 38, borderRadius: '12px', bgcolor: `${color}12`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
      </Box>
    </Box>
  );
};

// ── PeriodoCard ───────────────────────────────────────────────────────────────
const PeriodoCard = ({ anio, mes, setAnio, setMes }) => (
  <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}` }}>
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2,
      letterSpacing: '0.07em', textTransform: 'uppercase', mb: 1 }}>Periodo</Typography>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <input type="number" value={anio} onChange={e => setAnio(Number(e.target.value))}
        min="2020" max="2099"
        style={{ width: 64, height: 34, padding: '0 8px', border: `1px solid ${IOS.separator}`,
          borderRadius: 8, fontSize: '0.8rem', color: IOS.label, backgroundColor: IOS.surface,
          outline: 'none', fontFamily: 'inherit' }}
        onFocus={e => e.target.style.borderColor = IOS.blue}
        onBlur={e  => e.target.style.borderColor = IOS.separator} />
      <Sel value={mes} onChange={e => setMes(Number(e.target.value))} w={90}>
        {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m.slice(0, 3)}</option>)}
      </Sel>
    </Box>
  </Box>
);

// ── TH ────────────────────────────────────────────────────────────────────────
const TH = ({ children, center, right, w }) => (
  <th style={{ padding: '9px 10px', width: w,
    textAlign: center ? 'center' : right ? 'right' : 'left',
    fontSize: '0.6rem', fontWeight: 700, color: IOS.label2, letterSpacing: '0.06em',
    textTransform: 'uppercase', backgroundColor: IOS.surface,
    borderBottom: `1px solid ${IOS.separator}`, whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 1 }}>{children}</th>
);

// ── Badge Estado ──────────────────────────────────────────────────────────────
const BadgeEstado = ({ estado }) => {
  const activa = (estado || '').toUpperCase().includes('ACTIV') ||
                 (estado || '').toUpperCase().includes('ABIERTA');
  const color  = activa ? IOS.green : IOS.label2;
  const bg     = activa ? `${IOS.green}10` : IOS.separator;
  const bdr    = activa ? `${IOS.green}30` : IOS.separator;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 0.8, py: 0.25, borderRadius: '20px', bgcolor: bg, border: `1px solid ${bdr}` }}>
      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: color, flexShrink: 0,
        ...(activa && {
          animation: 'pulseGreen 1.8s ease-in-out infinite',
          '@keyframes pulseGreen': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } }
        }) }} />
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700,
        color: activa ? '#1A6B3C' : IOS.label2 }}>{estado || '—'}</Typography>
    </Box>
  );
};

// ── Modal exportar ────────────────────────────────────────────────────────────
const ModalExportar = ({ open, onClose, total, onExportar, exportando }) => {
  if (!open) return null;
  return (
    <Box sx={{ position:'fixed', inset:0, zIndex:9999, bgcolor:'rgba(0,0,0,0.4)',
      display:'flex', alignItems:'center', justifyContent:'center', p:2, backdropFilter:'blur(4px)' }}
      onClick={()=>!exportando&&onClose()}>
      <Box onClick={e=>e.stopPropagation()}
        sx={{ bgcolor:IOS.surface, borderRadius:'20px', overflow:'hidden',
          width:360, maxWidth:'92vw', boxShadow:'0 24px 60px rgba(0,0,0,0.2)' }}>
        <Box sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Box>
            <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
              letterSpacing:'0.07em', textTransform:'uppercase', mb:0.3 }}>Exportar</Typography>
            <Typography sx={{ fontSize:'0.95rem', fontWeight:700, color:IOS.label }}>
              Reporte de Sesiones
            </Typography>
          </Box>
          <Box component="button" onClick={onClose}
            sx={{ width:30, height:30, borderRadius:'50%', border:'none',
              bgcolor:'#E5E5EA', cursor:'pointer', display:'flex',
              alignItems:'center', justifyContent:'center', transition:'all 0.15s',
              '&:hover':{ bgcolor:'#D1D1D6' } }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </Box>
        </Box>
        <Box sx={{ p:2.5, display:'flex', gap:1.5 }}>
          {[
            { fmt:'pdf',   label:'PDF',   color:'#EF4444', light:'#FEE2E2',
              icon:<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
            { fmt:'excel', label:'Excel', color:'#059669', light:'#D1FAE5',
              icon:<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="12" y1="10" x2="12" y2="20"/></svg> },
          ].map(({ fmt, label, color, light, icon }) => (
            <Box key={fmt} component="button" onClick={()=>!exportando&&onExportar(fmt)}
              disabled={exportando}
              sx={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:1.2,
                py:2.5, px:1, borderRadius:'14px', border:`1px solid ${IOS.separator}`,
                bgcolor:exportando?IOS.bg:IOS.surface, cursor:exportando?'wait':'pointer',
                transition:'all 0.2s', outline:'none',
                '&:hover':{ bgcolor:light, borderColor:color, transform:exportando?'none':'translateY(-2px)',
                  boxShadow:exportando?'none':`0 8px 20px ${color}25` } }}>
              <Box sx={{ width:52, height:52, borderRadius:'14px', bgcolor:light,
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                {icon}
              </Box>
              <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:exportando?IOS.label2:color }}>
                {exportando?'Generando…':label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ px:2.5, pb:2.5, display:'flex', alignItems:'center', justifyContent:'center', gap:1 }}>
          <Box sx={{ width:6, height:6, borderRadius:'50%', bgcolor:IOS.green }} />
          <Typography sx={{ fontSize:'0.75rem', color:IOS.label2 }}>
            {total.toLocaleString()} sesiones a exportar
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
// ══════════════════════════════════════════════════════════════════════════════
const SesionesCaja = () => {
  const navigate = useNavigate();
  const user  = authService.getCurrentUser();
  const nf    = user?.nombreCompleto || user?.usuario || '';
  const ueId  = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;
  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const [anio, setAnio] = useState(hoyAnio());
  const [mes,  setMes]  = useState(hoyMes());
  const [busqueda, setBusqueda] = useState('');

  const [kpi,      setKpi]      = useState(null);
  const [grafico,  setGrafico]  = useState([]);
  const [sesiones, setSesiones] = useState([]);
  const [carg,     setCarg]     = useState(false);
  const [refresh,  setRefresh]  = useState(0);
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);

  const [modalPrint, setModalPrint] = useState(false);
const [pdfUrl, setPdfUrl] = useState(null);
const [cargandoPdf, setCargandoPdf] = useState(false);
const [formatoActivo, setFormatoActivo] = useState('A4');
const [idPrint, setIdPrint] = useState(null);
const [tipoGrafico, setTipoGrafico] = useState('area'); // 'area' | 'line' | 'bar'
const [modalExportar, setModalExportar] = useState(false);
const [exportando, setExportando] = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setDiaSeleccionado(null);
      setCarg(true);
      try {
        const p = { params: { periodo: anio, mes, idUe: ueId } };
        const [rKpi, rGraf, rSes] = await Promise.all([
          api.get('/caja/sesiones/kpi',     p),
          api.get('/caja/sesiones/grafico', p),
          api.get('/caja/sesiones/listar',  p),
        ]);
        if (cancelled) return;
        if (rKpi.data.success)  setKpi(rKpi.data.data);
        if (rGraf.data.success) setGrafico(rGraf.data.data || []);
        if (rSes.data.success)  setSesiones(rSes.data.data || []);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setCarg(false); }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [anio, mes, ueId, refresh]);

  const ejecutarExportar = async (fmt) => {
    setExportando(true);
    try {
      const mime = fmt==='pdf'?'application/pdf':'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const ext  = fmt==='pdf'?'pdf':'xlsx';
      const endpoint = `/caja/sesiones/exportar-${fmt}`;
      const filtros = { periodo:`${MESES[mes-1]} ${anio}`, cajero:busqueda||'Todos', totalRecaudado:String(totalRecaudado) };
      const r = await api.post(endpoint, { datos:sesionesFilt, filtros }, { responseType:'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data],{type:mime}));
      a.download = `SesionesCaja_${anio}${String(mes).padStart(2,'0')}.${ext}`;
      a.click(); URL.revokeObjectURL(a.href);
      setModalExportar(false);
    } catch(e){ console.error(e); } finally { setExportando(false); }
  };

  const abrirPdf = async (idApertura, formato='A4') => {
  if (!idApertura) return;

  setFormatoActivo(formato); // 🔥 CLAVE
  setCargandoPdf(true);
  setPdfUrl(null);
  setModalPrint(true);

  try {
    const r = await api.get('/caja/corte/reporte-pdf', {
      params: { idApertura, formato },
      responseType: 'blob'
    });

    const url = URL.createObjectURL(
      new Blob([r.data], { type: 'application/pdf' })
    );

    setPdfUrl(url);

  } catch (e) {
    console.error(e);
    setModalPrint(false);
  } finally {
    setCargandoPdf(false);
  }
};

  // ── Filtro ─────────────────────────────────────────────────────────────
  const sesionesFilt = useMemo(() => {
    let f = sesiones;
    if(diaSeleccionado){
      f = f.filter(s => {
        const dd = String(s.apertura||'').split('/')[0].padStart(2,'0');
        return dd === String(diaSeleccionado).padStart(2,'0');
      });
    }
    if(busqueda.trim()){
      const b = busqueda.toLowerCase();
      f = f.filter(s =>
        (s.cajero||'').toLowerCase().includes(b)||
        (s.estado||'').toLowerCase().includes(b)||
        (s.apertura||'').toLowerCase().includes(b)
      );
    }
    return f;
  },[sesiones, busqueda, diaSeleccionado]);

  // Total recaudado (único campo decimal real)
  const totalRecaudado = useMemo(() =>
    sesionesFilt.reduce((s, r) => s + Number(r.totalRecaudado || 0), 0),
  [sesionesFilt]);

  // ── Gráfico ────────────────────────────────────────────────────────────
  const esBarras = tipoGrafico === 'bar';
  const chartData = {
    labels: grafico.map(g => g.dia),
    datasets: [{
      label: 'Recaudación',
      data: grafico.map(g => Number(g.totalDia || 0)),
      borderColor: esBarras ? 'transparent' : IOS.blue,
      borderWidth: esBarras ? 0 : 2.5,
      borderRadius: esBarras ? 6 : 0,
      backgroundColor: ctx => {
        if (esBarras) return IOS.blue;
        if (!ctx.chart.chartArea) return `${IOS.blue}10`;
        const { top, bottom } = ctx.chart.chartArea;
        const grad = ctx.chart.ctx.createLinearGradient(0, top, 0, bottom);
        grad.addColorStop(0, `${IOS.blue}30`);
        grad.addColorStop(1, `${IOS.blue}01`);
        return grad;
      },
      fill: tipoGrafico === 'area', tension: 0.45,
      pointRadius: esBarras ? 0 : 3, pointHoverRadius: esBarras ? 0 : 6,
      pointBackgroundColor: IOS.blue, pointBorderColor: 'white', pointBorderWidth: 2,
    }]
  };
  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    animation: { duration: 700, easing: 'easeOutQuart' },
    interaction: { mode: 'index', axis: 'x', intersect: false },
    plugins: {
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(28,28,30,0.92)', titleColor: '#fff',
        bodyColor: 'rgba(255,255,255,0.75)', padding: 12, borderRadius: 12, displayColors: false,
        callbacks: {
          title: items => `Día ${items[0].label}`,
          label: ctx => ` S/ ${ctx.parsed.y.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`,
        }
      }
    },
    onClick: (evt, elements, chart) => {
      if(!elements.length) return;
      const dia = chart.data.labels[elements[0].index];
      setDiaSeleccionado(prev => prev===dia ? null : dia);
    },
    scales: {
      x: { grid: { display: false }, border: { display: false },
           ticks: { font: { size: 10 }, color: IOS.label2 } },
      y: { min: 0, border: { display: false },
           grid: { color: 'rgba(60,60,67,0.05)' },
           ticks: { callback: v => v === 0 ? 'S/0' : `S/${(v/1000).toFixed(0)}k`,
                    font: { size: 10 }, color: IOS.label2, maxTicksLimit: 5 } },
    },
  };

  // ── RENDER ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: IOS.bg }}>
      <HeaderIOS titulo="Sesiones de Caja" subtitulo="Caja"
        onHome={() => navigate('/caja')} onLogout={handleLogout}
        nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()} />

      <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 1.5, sm: 2.6 }, pt: 2.5, pb: 6 }}>

        {/* KPIs */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '160px repeat(4,1fr)' },
          gap: 1.5, mb: 2 }}>
          <PeriodoCard anio={anio} mes={mes} setAnio={setAnio} setMes={setMes} />

          <KpiCard label="Total mes" valor={kpi?.totalRecaudadoMes || 0} color={IOS.blue} esMonto
            sub={`${MESES[mes-1]} ${anio}`}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>} />

          <KpiCard label="Captación hoy" valor={kpi?.captacionDia || 0} color={IOS.green} esMonto
            sub="Recaudado del día"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="1.8" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>} />

          <KpiCard label="Aperturas activas" valor={kpi?.aperturasActivasHoy || 0} color={IOS.orange} esMonto={false}
            sub="Sesiones abiertas hoy"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>} />

          <KpiCard label="Cierres del mes" valor={kpi?.aperturasCerradasMes || 0} color={IOS.purple} esMonto={false}
            sub="Sesiones cerradas"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="1.8" strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>} />
        </Box>

        {/* Gráfico */}
        <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2.5, mb: 2,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}` }}>
          <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:0.2, flexWrap:'wrap', gap:1 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: IOS.label }}>
                Recaudación diaria
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: IOS.label2 }}>
                Total recaudado por día — {MESES[mes - 1]} {anio}
              </Typography>
            </Box>
            <Box sx={{ display:'flex', gap:0.4, bgcolor:IOS.bg, borderRadius:'10px', p:0.4 }}>
              {[
                { key:'area', tip:'Área', icon:<path d="M2 20l5-7 4 4 5-9 6 5v7H2z" fill="currentColor" opacity=".85"/> },
                { key:'line', tip:'Línea', icon:<path d="M2 18l5-8 4 5 5-11 6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/> },
                { key:'bar',  tip:'Barras', icon:<><rect x="3" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".9"/><rect x="10" y="6" width="4" height="12" rx="1" fill="currentColor" opacity=".9"/><rect x="17" y="3" width="4" height="15" rx="1" fill="currentColor" opacity=".9"/></> },
              ].map(({key,tip,icon})=>(
                <Box key={key} onClick={()=>setTipoGrafico(key)} title={tip}
                  sx={{ width:28, height:28, borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                    bgcolor:tipoGrafico===key?IOS.surface:'transparent',
                    boxShadow:tipoGrafico===key?'0 1px 3px rgba(0,0,0,0.12)':'none',
                    color:tipoGrafico===key?IOS.blue:IOS.label2 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24">{icon}</svg>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb:2 }} />
          <Box key={tipoGrafico} sx={{ height: 200, animation:'fadeInChart 0.35s ease' }}>
            {carg
              ? <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={20} sx={{ color: IOS.blue }} />
                </Box>
              : grafico.length === 0
                ? <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '0.78rem', color: IOS.label2 }}>Sin datos este mes</Typography>
                  </Box>
                : tipoGrafico==='bar'
                  ? <Bar key={`${anio}-${mes}-${tipoGrafico}`} data={chartData} options={chartOpts} />
                  : <Line key={`${anio}-${mes}-${tipoGrafico}`} data={chartData} options={chartOpts} />
            }
          </Box>
          {diaSeleccionado&&(
          <Box sx={{display:'flex',alignItems:'center',gap:1,mt:1}}>
            <Typography sx={{fontSize:'0.75rem',color:IOS.label2}}>
              Filtrando día: <strong style={{color:IOS.blue}}>{diaSeleccionado}</strong>
            </Typography>
            <Box component="button" onClick={()=>setDiaSeleccionado(null)}
              sx={{height:22,px:1,borderRadius:'6px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.bg,cursor:'pointer',fontSize:'0.68rem',color:IOS.label2}}>
              Limpiar
            </Box>
          </Box>
        )}
        </Box>

        {/* Tabla */}
        <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px',
          border: `1px solid ${IOS.separator}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>

          {/* Header */}
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${IOS.separator}`,
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ position: 'relative', flex: 1, minWidth: 180 }}>
              <Box sx={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
                color: IOS.label2, pointerEvents: 'none' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              </Box>
              <input placeholder="Buscar cajero, estado..."
                value={busqueda} onChange={e => setBusqueda(e.target.value)}
                style={{ width: '100%', height: 32, paddingLeft: 26, paddingRight: 8,
                  border: `1px solid ${IOS.separator}`, borderRadius: 8, fontSize: '0.76rem',
                  color: IOS.label, backgroundColor: IOS.bg, outline: 'none',
                  fontFamily: 'inherit', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = IOS.blue}
                onBlur={e  => e.target.style.borderColor = IOS.separator} />
            </Box>
            <Box component="button" onClick={() => setRefresh(r => r + 1)}
              sx={{ width: 30, height: 30, borderRadius: '50%', border: `1px solid ${IOS.separator}`,
                bgcolor: IOS.bg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                '&:hover': { bgcolor: `${IOS.blue}10` } }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2.2" strokeLinecap="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            </Box>
            <Box sx={{ px: 1, py: 0.3, bgcolor: IOS.bg, borderRadius: '20px', border: `1px solid ${IOS.separator}` }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: IOS.blue }}>
                {sesionesFilt.length} sesiones
              </Typography>
            </Box>
            {sesionesFilt.length>0 && (
              <Box component="button" onClick={()=>setModalExportar(true)}
                sx={{ width:30, height:30, borderRadius:'50%', border:'none',
                  bgcolor:IOS.green, cursor:'pointer', display:'flex',
                  alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                  '&:hover':{ filter:'brightness(0.9)', transform:'scale(1.05)' } }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/>
                </svg>
              </Box>
            )}
          </Box>

          {/* Contenido */}
          {carg
            ? <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: IOS.blue }} /></Box>
            : <>
            {/* Móvil — cards */}
            <Box sx={{ display:{ xs:'block', md:'none' }, maxHeight:500, overflowY:'auto',
              '&::-webkit-scrollbar':{ width:3 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
              {sesionesFilt.length===0
                ? <Box sx={{ py:6, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Sin sesiones en este período</Typography></Box>
                : sesionesFilt.map((r,i)=>(
                    <Box key={i} sx={{ p:2, borderBottom:`1px solid ${IOS.separator}` }}>
                      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:1 }}>
                        <Box sx={{ minWidth:0 }}>
                          <Typography sx={{ fontWeight:700, fontSize:'0.8rem', color:'#1F2937' }}>{r.cajero}</Typography>
                          <Typography sx={{ fontSize:'0.66rem', color:IOS.label2 }}>{r.apertura} → {r.cierre||'En curso'}</Typography>
                        </Box>
                        <BadgeEstado estado={r.estado} />
                      </Box>
                      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:0.6, mb:1 }}>
                        {[['Base',r.base_||'0.00',IOS.label2],['Boletas',r.boletas||'—',IOS.blue],['Facturas',r.facturas||'—',IOS.orange],
                          ['Notas C.',r.notasCredito||'—',IOS.red],['Ticket',r.ticket||'—',IOS.purple],['Tk.Anul.',r.ticketsAnulados||'—',IOS.label2]]
                          .map(([l,v,c],j)=>(
                          <Box key={j} sx={{ bgcolor:IOS.bg, borderRadius:'8px', px:0.5, py:0.5, textAlign:'center' }}>
                            <Typography sx={{ fontSize:'0.55rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>{l}</Typography>
                            <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color:c }}>{v}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <Box>
                          <Typography sx={{ fontSize:'0.55rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>Total + Base</Typography>
                          <Typography sx={{ fontSize:'0.92rem', fontWeight:800, color:IOS.label }}>{Number(r.totalRecaudado||0).toFixed(2)}</Typography>
                        </Box>
                        <Box onClick={()=>{ setIdPrint(r.idApertura); abrirPdf(r.idApertura,'A4'); }}
                          sx={{ width:32, height:32, borderRadius:'50%', bgcolor:`${IOS.green}15`,
                            display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round">
                            <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                          </svg>
                        </Box>
                      </Box>
                    </Box>
                  ))
              }
              {sesionesFilt.length>1 && (
                <Box sx={{ p:2, bgcolor:`${IOS.blue}06`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>Totales — {sesionesFilt.length} sesiones</Typography>
                  <Typography sx={{ fontSize:'0.92rem', fontWeight:800, color:IOS.blue }}>{totalRecaudado.toLocaleString('es-PE',{minimumFractionDigits:2})}</Typography>
                </Box>
              )}
            </Box>

            {/* Desktop — tabla */}
            <Box sx={{ display:{ xs:'none', md:'block' }, overflowX: 'auto', overflowY: 'auto', maxHeight: 500,
                '&::-webkit-scrollbar': { width: 4, height: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: IOS.separator, borderRadius: 4 } }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', minWidth: 900 }}>
                  <thead>
                    <tr>
                      <TH center w={90}>Estado</TH>
                      <TH w={180}>Cajero</TH>
                      <TH w={140}>Apertura</TH>
                      <TH w={140}>Cierre</TH>
                      <TH right w={65}>Base</TH>
                      <TH right w={140}>Boletas</TH>
                      <TH right w={140}>Facturas</TH>
                      <TH right w={140}>Notas C.</TH>
                      <TH right w={140}>Ticket</TH>
                      <TH right w={140}>Tick. Anul.</TH>
                      <TH right w={100}>Total + Base</TH>
                      <TH center w={60}>Acción</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {sesionesFilt.length === 0
                      ? <tr><td colSpan={12} style={{ padding: '40px', textAlign: 'center',
                          color: IOS.label2, fontSize: '0.8rem' }}>
                          Sin sesiones en este período
                        </td></tr>
                      : <>
                          {sesionesFilt.map((r, i) => (
                            <tr key={i}
                              style={{ borderBottom: `1px solid ${IOS.separator}`,
                                backgroundColor: IOS.surface, transition: 'background 0.1s' }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = IOS.bg}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = IOS.surface}>

                              <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                <BadgeEstado estado={r.estado} />
                              </td>
                              <td style={{ padding: '7px 10px', fontWeight: 600,
                                fontSize: '0.73rem', color: IOS.label }}>{r.cajero}</td>
                              <td style={{ padding: '7px 10px', fontSize: '0.68rem',
                                color: IOS.label2, whiteSpace: 'nowrap' }}>{r.apertura}</td>
                              <td style={{ padding: '7px 10px', fontSize: '0.68rem',
                                color: IOS.label2, whiteSpace: 'nowrap' }}>{r.cierre || '—'}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'right',
                                fontSize: '0.7rem', color: IOS.label2 }}>{r.base_ || '0.00'}</td>

                              {/* Columnas texto formateado "# N - S/. X" */}
                              <td style={{ padding: '7px 10px', textAlign: 'right',
                                fontSize: '0.72rem', color: IOS.blue, fontWeight: 600 }}>
                                {r.boletas || '—'}
                              </td>
                              <td style={{ padding: '7px 10px', textAlign: 'right',
                                fontSize: '0.72rem', color: IOS.orange, fontWeight: 600 }}>
                                {r.facturas || '—'}
                              </td>
                              <td style={{ padding: '7px 10px', textAlign: 'right',
                                fontSize: '0.72rem', color: IOS.red }}>
                                {r.notasCredito || '—'}
                              </td>
                              <td style={{ padding: '7px 10px', textAlign: 'right',
                                fontSize: '0.72rem', color: IOS.purple }}>
                                {r.ticket || '—'}
                              </td>
                              <td style={{ padding: '7px 10px', textAlign: 'right',
                                fontSize: '0.7rem', color: IOS.label2 }}>
                                {r.ticketsAnulados || '—'}
                              </td>

                              {/* Total decimal real */}
                              <td style={{ padding: '7px 10px', textAlign: 'right',
                                fontWeight: 800, fontSize: '0.78rem', color: IOS.label }}>
                                {Number(r.totalRecaudado || 0).toFixed(2)}
                              </td>

                              <td style={{ padding: '7px 10px', textAlign:'center' }}>
                              <Box
                                onClick={() => {
  setIdPrint(r.idApertura);
  abrirPdf(r.idApertura, 'A4');
}}
                                sx={{
                                  width:28,
                                  height:28,
                                  borderRadius:'50%',
                                  bgcolor:`${IOS.green}15`,
                                  display:'inline-flex',
                                  alignItems:'center',
                                  justifyContent:'center',
                                  cursor:'pointer',
                                  transition:'all 0.15s',
                                  '&:hover':{
                                    bgcolor:`${IOS.green}30`,
                                    transform:'scale(1.1)'
                                  }
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24"
                                  fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round">
                                  <polyline points="6 9 6 2 18 2 18 9"/>
                                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                                  <rect x="6" y="14" width="12" height="8"/>
                                </svg>
                              </Box>
                            </td>

                            </tr>
                          ))}

                          {/* Fila totales — solo totalRecaudado es sumable */}
                          {sesionesFilt.length > 1 && (
                            <tr style={{ backgroundColor: `${IOS.blue}06`,
                              borderTop: `2px solid ${IOS.blue}20` }}>
                              <td colSpan={10} style={{ padding: '8px 10px' }}>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700,
                                  color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  Totales — {sesionesFilt.length} sesiones
                                </Typography>
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'right',
                                fontWeight: 800, fontSize: '0.82rem', color: IOS.blue }}>
                                {totalRecaudado.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          )}
                        </>
                    }
                  </tbody>
                </table>
              </Box>
            </>
          }
        </Box>
        
            </Box>

      <ModalExportar open={modalExportar} onClose={()=>setModalExportar(false)}
        total={sesionesFilt.length} exportando={exportando} onExportar={ejecutarExportar} />

      {/* 🔥 MODAL DE IMPRESIÓN */}
      {modalPrint && (
      <Box
        sx={{
          position:'fixed',
          inset:0,
          zIndex:9999,
          bgcolor:'rgba(0,0,0,0.7)',
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
          p:2,
          backdropFilter:'blur(4px)'
        }}
        onClick={()=>{ setModalPrint(false); setPdfUrl(null); }}
      >
        <Box
          onClick={e=>e.stopPropagation()}
          sx={{
            bgcolor:IOS.surface,
            borderRadius:'20px',
            overflow:'hidden',
            width:'85vw',          // 🔥 MÁS ANGOSTO
            maxWidth:900,          // 🔥 CONTROL REAL
            height:'88vh',
            display:'flex',
            flexDirection:'column',
            boxShadow:'0 24px 60px rgba(0,0,0,0.4)'
          }}
        >

          {/* HEADER PRO */}
          <Box sx={{
            px:2.5,
            pt:2,
            pb:1.5,
            borderBottom:`1px solid ${IOS.separator}`,
            display:'flex',
            alignItems:'center',
            justifyContent:'space-between'
          }}>

            {/* TITULO */}
            <Box>
              <Typography sx={{
                fontWeight:700,
                fontSize:'0.9rem',
                color:IOS.label
              }}>
                Vista previa — Sesión
              </Typography>

              <Typography sx={{
                fontSize:'0.72rem',
                color:IOS.label2
              }}>
                Reporte de corte
              </Typography>
            </Box>

            {/* BOTONES */}
            <Box sx={{display:'flex',gap:1,alignItems:'center'}}>

              {/* A4 / TICKET */}
              {pdfUrl && ['A4','TICKET'].map(f => (
                <Box key={f}
                  component="button"
                  onClick={() => abrirPdf(idPrint, f)}
                  sx={{
                    height:30,
                    px:1.4,
                    borderRadius:'8px',
                    border:'none',
                    cursor:'pointer',
                    bgcolor:formatoActivo===f ? `${IOS.blue}15` : 'transparent',
                    outline:formatoActivo===f
                      ? `1.5px solid ${IOS.blue}`
                      : `1px solid ${IOS.separator}`,
                    transition:'all 0.15s'
                  }}
                >
                  <Typography sx={{
                    fontSize:'0.72rem',
                    fontWeight:700,
                    color:formatoActivo===f ? IOS.blue : IOS.label2
                  }}>
                    {f}
                  </Typography>
                </Box>
              ))}

              {/* DESCARGAR */}
              {pdfUrl && (
                <Box component="a"
                  href={pdfUrl}
                  download={`Sesion_${idPrint}_${formatoActivo}.pdf`}
                  sx={{
                    height:32,
                    px:1.5,
                    borderRadius:'10px',
                    border:`1px solid ${IOS.green}30`,
                    bgcolor:`${IOS.green}08`,
                    cursor:'pointer',
                    color:IOS.green,
                    fontWeight:700,
                    fontSize:'0.78rem',
                    textDecoration:'none',
                    display:'flex',
                    alignItems:'center',
                    gap:0.5,
                    '&:hover':{bgcolor:`${IOS.green}14`}
                  }}
                >
                  Descargar
                </Box>
              )}

              {/* CERRAR */}
              <Box component="button"
                onClick={()=>{ setModalPrint(false); setPdfUrl(null); }}
                sx={{
                  width:30,
                  height:30,
                  borderRadius:'50%',
                  border:'none',
                  bgcolor:'#E5E5EA',
                  cursor:'pointer',
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'center'
                }}
              >
                ✕
              </Box>

            </Box>
          </Box>

          {/* PDF */}
          <Box sx={{flex:1, bgcolor:'#525659'}}>
            {cargandoPdf
              ? <Box sx={{
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'center',
                  height:'100%'
                }}>
                  <CircularProgress sx={{color:'white'}}/>
                </Box>
              : pdfUrl
                ? <iframe
                    src={pdfUrl}
                    style={{width:'100%',height:'100%',border:'none'}}
                  />
                : <Box sx={{
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    height:'100%'
                  }}>
                    <Typography sx={{color:'white'}}>
                      No se pudo generar
                    </Typography>
                  </Box>
            }
          </Box>

        </Box>
      </Box>
    )}

    <style>{`
        @keyframes fadeInChart {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </Box>
  );
};

export default SesionesCaja;
